'use client';

import { createClient } from '@/lib/supabase/client';

export interface ChieftainshipRow {
  id: string;
  name: string;
  alternate_names: string[];
  district: string;
  province: string;
  historical_territory: string;
  clan_lineage: string;
  natural_boundaries: string;
  historical_notes: string;
  era: string;
  coord_x: number;
  coord_y: number;
  verified: boolean;
  created_at: string;
}

export interface ChieftainshipFilters {
  search?: string;
  province?: string;
  verified?: boolean;
}

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '08') return true;
  }
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
    ];
    return schemaErrorPatterns.some(p => p.test(error.message));
  }
  return false;
}

export async function fetchChieftainships(
  filters: ChieftainshipFilters = {}
): Promise<ChieftainshipRow[]> {
  const supabase = createClient();

  try {
    let query = supabase
      .from('chieftainships')
      .select('*')
      .order('province', { ascending: true })
      .order('name', { ascending: true });

    if (filters.verified !== undefined) {
      query = query.eq('verified', filters.verified);
    }

    if (filters.province && filters.province !== 'all') {
      query = query.eq('province', filters.province);
    }

    if (filters.search && filters.search.trim()) {
      const term = `%${filters.search.trim()}%`;
      query = query.or(
        `name.ilike.${term},district.ilike.${term},province.ilike.${term},clan_lineage.ilike.${term},historical_territory.ilike.${term}`
      );
    }

    const { data, error } = await query;

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error fetching chieftainships:', error.message);
        throw error;
      }
      console.log('Data error fetching chieftainships:', error.message);
      return [];
    }

    return data || [];
  } catch (error: any) {
    console.log('Error fetching chieftainships:', error.message);
    throw error;
  }
}

export async function fetchChieftainshipById(id: string): Promise<ChieftainshipRow | null> {
  const supabase = createClient();

  try {
    const { data, error } = await supabase
      .from('chieftainships')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error fetching chieftainship:', error.message);
        throw error;
      }
      console.log('Data error fetching chieftainship:', error.message);
      return null;
    }

    return data;
  } catch (error: any) {
    console.log('Error fetching chieftainship:', error.message);
    throw error;
  }
}

export async function fetchChieftainshipProvinces(): Promise<string[]> {
  const supabase = createClient();

  try {
    const { data, error } = await supabase
      .from('chieftainships')
      .select('province')
      .order('province', { ascending: true });

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error fetching provinces:', error.message);
        throw error;
      }
      return [];
    }

    const unique = Array.from(new Set((data || []).map(r => r.province)));
    return unique;
  } catch (error: any) {
    console.log('Error fetching provinces:', error.message);
    return [];
  }
}
